/**
 * Plausible roguelike-layer data: what the `?ui=<name>` fixture routes show (08 intro) and what the
 * menu tests run on, through the in-memory `GameApi` in fakeApi.ts. No storage, no mission.
 */

import type { MemorialEntry, Operation, Profile, RangerRecord, ServiceEntry, TheatreNode } from '../../../meta/types';
import type { ArchetypeId, Biome, MissionParams, MissionResult } from '../../../sim/types';

export function ranger(id: string, overrides: Partial<RangerRecord> = {}): RangerRecord {
  return {
    id,
    seed: id.length * 7919 + id.charCodeAt(0),
    firstName: 'Dale',
    surname: 'Marlowe',
    callsign: 'Viper',
    rank: 0,
    merit: 0,
    perks: [],
    traits: ['green'],
    medals: [],
    record: { missions: 0, successes: 0, kills: 0, knifeKills: 0, woundsTaken: 0, timesCaptured: 0 },
    status: 'ready',
    recoveryMissions: 0,
    fatigue: 0,
    veteran: false,
    pendingPerkChoices: [],
    traitTimers: {},
    ...overrides,
  };
}

export function recruits(): RangerRecord[] {
  return [
    ranger('r1', { firstName: 'Dale', surname: 'Marlowe', callsign: 'Viper', traits: ['farm-strong'] }),
    ranger('r2', { firstName: 'Inez', surname: 'Okafor', callsign: 'Wren', traits: ['hunter'] }),
    ranger('r3', { firstName: 'Tomas', surname: 'Reyes', callsign: 'Brick', traits: ['hothead'] }),
    ranger('r4', { firstName: 'Anja', surname: 'Lindqvist', callsign: 'Frost', traits: ['swimmer'] }),
  ];
}

export function veterans(): RangerRecord[] {
  return [
    ranger('v1', {
      firstName: 'Sam',
      surname: 'Kowalski',
      callsign: 'Deacon',
      rank: 4,
      merit: 15200,
      perks: ['ghost', 'knife-work', 'sapper'],
      traits: ['lucky', 'blooded'],
      medals: ['commendation-1', 'bronze-star', 'purple-heart'],
      record: { missions: 14, successes: 11, kills: 96, knifeKills: 31, woundsTaken: 4, timesCaptured: 1 },
    }),
    ranger('v2', {
      firstName: 'Ruth',
      surname: 'Abara',
      callsign: 'Halo',
      rank: 8,
      merit: 52000,
      perks: ['steady-hands', 'deadeye', 'iron-lungs', 'field-medic', 'pathfinder'],
      traits: ['decorated'],
      medals: ['commendation-1', 'commendation-2', 'silver-star', 'dsc', 'pow-medal'],
      record: { missions: 31, successes: 27, kills: 240, knifeKills: 44, woundsTaken: 7, timesCaptured: 1 },
    }),
  ];
}

export function params(archetype: ArchetypeId, biome: Biome, overrides: Partial<MissionParams> = {}): MissionParams {
  return {
    archetype,
    biome,
    timeOfDay: 'day',
    weather: 'clear',
    threat: 4,
    seed: 1987,
    modifiers: [],
    intel: [],
    ...overrides,
  };
}

function node(id: string, column: number, row: number, overrides: Partial<TheatreNode>): TheatreNode {
  return {
    id,
    theatre: 0,
    column,
    row,
    kind: 'mission',
    params: null,
    keystone: false,
    variant: false,
    rewardRp: 12,
    rewardMultiplier: 1,
    links: [],
    state: 'hidden',
    ...overrides,
  };
}

/**
 * Theatre 0: four columns and a keystone, with support nodes and a variant; one node of theatre 1. Three of
 * its missions are the expansion's (10 §2), chosen for their cards: Beacon (POD A, QUIET), Supply Train
 * (TIMED) and, in theatre 1, Eliminate the General.
 */
export function theatreNodes(): TheatreNode[] {
  return [
    node('n0a', 0, 0, { params: params('radar-array', 'arctic', { threat: 5 }), links: ['n1a', 'n1b'], state: 'open' }),
    node('n0b', 0, 1, {
      params: params('munitions-depot', 'arctic', { threat: 3, modifiers: ['night-op'], timeOfDay: 'night' }),
      variant: true,
      rewardMultiplier: 1.32,
      links: ['n1b', 'n1c'],
      state: 'open',
    }),
    node('n1a', 1, 0, { params: params('photograph-aircraft', 'arctic'), links: ['n2a'], state: 'scouted' }),
    node('n1b', 1, 1, { kind: 'rest', rewardRp: 0, links: ['n2a', 'n2b'], state: 'scouted' }),
    node('n1c', 1, 2, {
      params: params('beacon', 'arctic', { threat: 5, weather: 'blizzard', modifiers: ['storm', 'short-fuse'] }),
      rewardMultiplier: 1.56,
      links: ['n2b'],
      state: 'scouted',
    }),
    node('n2a', 2, 0, { kind: 'supply', rewardRp: 15, links: ['n3a', 'n3b'] }),
    node('n2b', 2, 1, { params: params('sam-site', 'arctic', { threat: 6 }), variant: true, links: ['n3b'] }),
    node('n3a', 3, 0, { params: params('supply-train', 'arctic', { threat: 5 }), links: ['k0'] }),
    node('n3b', 3, 1, { kind: 'intercept', rewardRp: 0, links: ['k0'] }),
    node('k0', 4, 0, {
      params: params('pipeline', 'arctic', { threat: 7 }),
      keystone: true,
      variant: true,
      rewardRp: 30,
    }),
    node('t1n0', 0, 0, { theatre: 1, params: params('eliminate-general', 'desert', { threat: 6 }) }),
  ];
}

export function operation(overrides: Partial<Operation> = {}): Operation {
  return {
    seed: 4242,
    codename: 'IRON LANTERN',
    difficulty: 1,
    hazard: 0,
    theatres: ['arctic', 'desert', 'temperate'],
    nodes: theatreNodes(),
    position: null,
    company: [
      ...recruits(),
      ranger('r5', { firstName: 'Olu', surname: 'Bright', callsign: 'Tally', status: 'wounded', recoveryMissions: 1 }),
      ranger('r6', { firstName: 'Kit', surname: 'Navarro', callsign: 'Moth', status: 'pow' }),
    ],
    rp: 30,
    readiness: 3,
    keystoneFailures: 0,
    status: 'active',
    log: ['OPERATION IRON LANTERN IS GO.'],
    cadet: false,
    ...overrides,
  };
}

/**
 * NEWEST FIRST, as a real profile holds them: `toMemorial` unshifts each name. This used to be
 * built oldest-first, which is why nobody noticed that the wall reversed it before drawing - the
 * fixture and the screen were wrong in opposite directions and cancelled out. Bea (IRON LANTERN,
 * the operation in progress) died after Joss (COLD HARBOR, the one before it).
 */
export function memorialEntries(): MemorialEntry[] {
  return [
    {
      ranger: ranger('m2', { firstName: 'Bea', surname: 'Santos', callsign: 'Lark', rank: 1, status: 'lost' }),
      fate: 'lost',
      operation: 'IRON LANTERN',
      mission: 'Cut a Pipeline',
      cause: 'POW - NEVER RECOVERED',
      epitaph: 'Nobody came in time.',
    },
    {
      ranger: ranger('m1', {
        firstName: 'Joss',
        surname: 'Hale',
        callsign: 'Pike',
        rank: 2,
        status: 'kia',
        medals: ['purple-heart'],
      }),
      fate: 'kia',
      operation: 'COLD HARBOR',
      mission: 'Knock Out Enemy Radar Array',
      cause: 'THIN ICE',
      epitaph: 'He ran light, as ordered.',
    },
  ];
}

/**
 * Service files for the fixture: enough of a career on the veteran to show what the screen is for -
 * a promotion, a decoration, a mission he failed, and one he was carried off. Keyed by ranger id,
 * oldest first, exactly as `recordFlight` writes them.
 */
function serviceFiles(): Record<string, ServiceEntry[]> {
  const flight = (over: Partial<ServiceEntry>): ServiceEntry => ({
    operation: 'COLD HARBOR', mission: 'Destroy a Munitions Depot (Desert)', archetype: 'munitions-depot',
    threat: 3, outcome: 'extracted', objectiveComplete: true, score: 2400, kills: 6, wounds: 0, medals: [],
    ...over,
  });
  return {
    v1: [
      flight({ mission: 'Knock Out Enemy Radar Array (Arctic)', archetype: 'radar-array', score: 1950, kills: 4 }),
      flight({ threat: 5, score: 3100, kills: 11, wounds: 1, medals: ['commendation-1'], promotedTo: 3 }),
      flight({
        operation: 'IRON LANTERN', mission: 'Blow a Bridge (Temperate)', archetype: 'blow-bridge', threat: 6,
        outcome: 'walkedOut', objectiveComplete: false, score: 0, kills: 2, wounds: 2,
      }),
      flight({
        operation: 'IRON LANTERN', mission: 'Rescue the Prisoners (Temperate)', archetype: 'pow-camp', threat: 7,
        score: 3400, kills: 9, wounds: 2, medals: ['bronze-star', 'purple-heart'], promotedTo: 4,
      }),
    ],
    r1: [flight({ operation: 'IRON LANTERN', threat: 2, score: 1200, kills: 3 })],
    r3: [flight({
      operation: 'IRON LANTERN', mission: 'Cut a Pipeline (Arctic)', archetype: 'pipeline', threat: 2,
      outcome: 'captured', objectiveComplete: false, score: 0, kills: 1, wounds: 3,
    })],
  };
}

export function profile(withOperation: boolean): Profile {
  return {
    version: 1,
    commendations: 7,
    flights: serviceFiles(),
    unlocks: [],
    hall: veterans(),
    retired: [ranger('x1', { firstName: 'Abe', surname: 'Stone', callsign: 'Anvil', rank: 6, status: 'retired' })],
    memorial: memorialEntries(),
    operation: withOperation ? operation() : null,
    classicRoster: [
      ranger('c1', { firstName: 'Max', surname: 'Power', callsign: 'Volt', rank: 3, merit: 9000 }),
      ranger('c2', { firstName: 'Ida', surname: 'Moss', callsign: 'Fern', status: 'kia' }),
    ],
    bestScores: { 'munitions-depot': 3400, 'blow-bridge': 2650 },
    operationsWon: 1,
    hazardUnlocked: 1,
  };
}

export function missionResult(overrides: Partial<MissionResult> = {}): MissionResult {
  return {
    outcome: 'extracted',
    objectiveComplete: true,
    lines: [
      { label: 'PRIMARY OBJECTIVE', points: 1000 },
      { label: 'EXTRACTED', points: 300 },
      { label: 'KILLS', points: 175 },
      { label: 'SECONDS LEFT', points: 96 },
    ],
    multiplier: 1.45,
    total: 2278,
    secondsLeft: 96,
    ...overrides,
  };
}
